// File: components/StreetFood.tsx
import React, { useState, useEffect, useRef } from 'react';
import { MapPin, DollarSign, Users, ChefHat, Utensils, Cookie, Coffee, Heart, Star } from 'lucide-react';

import { STREET_FOOD_CONFIGS, RANKS } from '../constants';
import { FoodItem, StreetFoodQuest, IdolRank } from '../types';
import { ExitButton } from './ExitButton';
import { CartoonEffectCanvas } from './CartoonEffectCanvas';

interface StreetFoodStandProps {
  onNavigate: (view: any) => void;
  cityKey: string;
  cash: number;
  fans: number;
  playerRank: IdolRank;
  onUpdateStats: (stats: { cash: number, fans: number }) => void;
}

const getStandIcon = (iconName: string) => {
  switch (iconName) {
    case 'ChefHat': return <ChefHat className="text-orange-400" />;
    case 'Utensils': return <Utensils className="text-pink-400" />;
    case 'Cookie': return <Cookie className="text-yellow-400" />;
    case 'Star': return <Star className="text-green-400" />;
    case 'Heart': return <Heart className="text-red-400" />;
    default: return <Utensils className="text-blue-400" />;
  }
};

// City-specific street food configurations

export const StreetFood: React.FC<StreetFoodStandProps> = ({
  onNavigate,
  cityKey,
  cash,
  fans,
  playerRank,
  onUpdateStats
}) => {
  const [quests, setQuests] = useState<StreetFoodQuest[]>(() => {
    const cityConfig = STREET_FOOD_CONFIGS[cityKey] || STREET_FOOD_CONFIGS['DEFAULT'];
    return cityConfig.quests;
  });
  const [config, setConfig] = useState(() => STREET_FOOD_CONFIGS[cityKey] || STREET_FOOD_CONFIGS['DEFAULT']);
  const [isConfigLoaded, setIsConfigLoaded] = useState(true);
  const [progressWidth, setProgressWidth] = useState('0%');
  const [selectedQuest, setSelectedQuest] = useState<StreetFoodQuest | null>(null);
  const [activeQuest, setActiveQuest] = useState<StreetFoodQuest | null>(null);
  const [questProgress, setQuestProgress] = useState(0);
  const [eatenFoodItems, setEatenFoodItems] = useState<FoodItem[]>([]);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionStats, setCompletionStats] = useState<{
    foodCount: number,
    totalCalories: number,
    totalProtein: number,
    totalCarbs: number,
    totalFat: number,
    reward: number,
    fanReward: number
  }>();
  const [completedQuests, setCompletedQuests] = useState<Set<string>>(new Set());
  const [imageLoadingStates, setImageLoadingStates] = useState<Record<string, 'loading' | 'direct' | 'proxy' | 'emoji'>>({});

  const handleImageLoad = (foodId: string, method: 'direct' | 'proxy') => {
    setImageLoadingStates(prev => ({ ...prev, [foodId]: method }));
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>, foodId: string, foodImage: string, foodEmoji: string) => {
    const img = e.target as HTMLImageElement;
    console.log(`Image error for ${foodId}:`, foodImage);

    if (!img.dataset.triedProxy) {
      // Try with CORS proxy
      console.log(`Trying proxy for ${foodId}`);
      img.dataset.triedProxy = 'true';
      img.src = `https://api.allorigins.win/raw?url=${encodeURIComponent(foodImage)}`;
      setImageLoadingStates(prev => ({ ...prev, [foodId]: 'proxy' }));
    } else {
      console.log(`Proxy failed for ${foodId}, falling back to emoji`);
      // Final fallback to emoji - safer approach
      setImageLoadingStates(prev => ({ ...prev, [foodId]: 'emoji' }));
      if (img.parentElement) {
        img.parentElement.innerHTML = `<span class="text-4xl">${foodEmoji || '🍽️'}</span>`;
      } else {
        // If parentElement is null, replace the img itself
        img.style.display = 'none';
        const emojiSpan = document.createElement('span');
        emojiSpan.className = 'text-4xl';
        emojiSpan.textContent = foodEmoji || '🍽️';
        img.parentNode?.insertBefore(emojiSpan, img);
      }
    }
  };
  const isMounted = useRef(true);

  // Master cleanup effect to ensure all timers are cleared on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Update progress width after mount to avoid hydration issues
  useEffect(() => {
    setProgressWidth(`${questProgress}%`);
  }, [questProgress]);



  // Update config when cityKey changes
  useEffect(() => {
    const cityConfig = STREET_FOOD_CONFIGS[cityKey] || STREET_FOOD_CONFIGS['DEFAULT'];
    setConfig(cityConfig);
    setQuests(cityConfig.quests);
  }, [cityKey]);

  const activeQuestRef = useRef<StreetFoodQuest | null>(null);
  const questProgressRef = useRef<number>(0);
  const eatenFoodItemsRef = useRef<FoodItem[]>([]);

  useEffect(() => { activeQuestRef.current = activeQuest; }, [activeQuest]);
  useEffect(() => { questProgressRef.current = questProgress; }, [questProgress]);
  useEffect(() => { eatenFoodItemsRef.current = eatenFoodItems; }, [eatenFoodItems]);

  // No timer needed, progress is manual or instant

  useEffect(() => {
    if (!activeQuest) return;

    const foodItemsToAdd = activeQuest.foodItems.length;

    if (foodItemsToAdd > eatenFoodItems.length && eatenFoodItems.length < activeQuest.foodItems.length) {
      const nextFoodItem = activeQuest.foodItems[eatenFoodItems.length];
      setEatenFoodItems(prev => [...prev, nextFoodItem]);
    }
  }, [questProgress, activeQuest, eatenFoodItems.length]);

  const startQuest = (quest: StreetFoodQuest) => {
    if (quest.requirements.some(req => !completedQuests.has(req))) {
      return; // Requirements not met
    }

    // Apply rank multiplier to rewards - with safety fallback
    const rankData = RANKS[playerRank] || RANKS[IdolRank.TRAINEE];
    const multiplier = rankData?.multiplier || 1;
    const cashReward = (quest?.reward || 0) * multiplier;
    const fanReward = (quest?.fanReward || 0) * multiplier;

    // Calculate nutrition for completion stats
    const totalCalories = quest.foodItems.reduce((sum, item) => sum + item.calories, 0);
    const totalProtein = quest.foodItems.reduce((sum, item) => sum + item.protein, 0);
    const totalCarbs = quest.foodItems.reduce((sum, item) => sum + item.carbs, 0);
    const totalFat = quest.foodItems.reduce((sum, item) => sum + item.fat, 0);

    setCompletionStats({
      foodCount: quest.foodItems.length,
      totalCalories,
      totalProtein,
      totalCarbs,
      totalFat,
      reward: cashReward,
      fanReward: fanReward
    });

    // Start quest
    setActiveQuest(quest);
    setQuestProgress(100);
    setEatenFoodItems([]);

    // Mark quest as in progress in internal state
    const updatedQuests = quests.map(q =>
      q.id === quest.id ? { ...q, completed: false } : q
    );
    setQuests(updatedQuests);
  };

  const completeQuest = () => {
    if (activeQuest) {
      const updatedQuests = quests.map(q =>
        q.id === activeQuest.id ? { ...q, completed: true } : q
      );
      setQuests(updatedQuests);

      // Apply rank multiplier to rewards - with safety fallback
      const rankData = RANKS[playerRank] || RANKS[IdolRank.TRAINEE];
      const multiplier = rankData?.multiplier || 1;
      const cashReward = (activeQuest?.reward || 0) * multiplier;
      const fanReward = (activeQuest?.fanReward || 0) * multiplier;

      onUpdateStats({ cash: cashReward, fans: fanReward });

      // Calculate total nutrition
      const totalCalories = eatenFoodItems.reduce((sum, item) => sum + item.calories, 0);
      const totalProtein = eatenFoodItems.reduce((sum, item) => sum + item.protein, 0);
      const totalCarbs = eatenFoodItems.reduce((sum, item) => sum + item.carbs, 0);
      const totalFat = eatenFoodItems.reduce((sum, item) => sum + item.fat, 0);

      // Show completion modal instead of alert
      setCompletionStats({
        foodCount: eatenFoodItems.length,
        totalCalories,
        totalProtein,
        totalCarbs,
        totalFat,
        reward: cashReward,
        fanReward: fanReward
      });
      setShowCompletionModal(true);

      setActiveQuest(null);
      setQuestProgress(0);
      setEatenFoodItems([]);
    }
  };



  const manualCompleteQuest = () => {
    if (activeQuest) {
      completeQuest();
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'EASY': return 'text-green-400';
      case 'MEDIUM': return 'text-yellow-400';
      case 'HARD': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getQuestIcon = (questId: string) => {
    return <ChefHat className="text-orange-400" />;
  };

  if (activeQuest) {
    const isCompleted = questProgress === 100;

    return (
      <div className="h-full w-full bg-slate-950 flex flex-col items-center justify-center p-4">
        <div className="w-full glass-panel rounded-lg shadow-2xl p-6 max-h-[90vh] overflow-y-auto">
          <h2 className="text-2xl font-bold text-white mb-4 text-center">
            {config.cuisine} Street Food Adventure
          </h2>

          {isCompleted && completionStats && (
            <div className="text-center mb-6">
              <div className="text-4xl mb-2">🎉</div>
              <h3 className="text-xl font-bold text-green-400 mb-2">Quest Complete!</h3>
              <div className="text-sm text-white/80">
                <span className="text-yellow-300">💰 ${completionStats?.reward || 0}</span> •
                <span className="text-pink-300"> +{completionStats?.fanReward || 0} fans</span>
                {RANKS[playerRank]?.multiplier > 1 && (
                  <span className="text-cyan-300 ml-2 text-xs">({RANKS[playerRank]?.multiplier}x rank multiplier)</span>
                )}
              </div>
            </div>
          )}

          <div className="text-center mb-6">
            <div className="flex justify-between items-center mb-4">
              <div className="text-left">
                <p className="text-slate-300">Status</p>
                <p className={`text-2xl font-bold ${isCompleted ? 'text-green-400' : 'text-blue-400'}`}>
                  {isCompleted ? 'Completed!' : 'In Progress'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-slate-300">Portions</p>
                <p className="text-2xl font-bold text-blue-400">{eatenFoodItems.length}/{activeQuest.foodItems.length}</p>
              </div>
            </div>

            <div className="w-full bg-slate-700 rounded-full h-4 overflow-hidden mb-4">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500"
                style={{ width: progressWidth }}
              />
            </div>
          </div>

          {isCompleted && (
            <button
              onClick={() => {
                setActiveQuest(null);
                setQuestProgress(0);
                setEatenFoodItems([]);
              }}
              className="fixed top-[120px] right-6 flex items-center gap-2 text-green-400 hover:text-white z-[400] transition-all bg-slate-900/80 hover:bg-green-700/80 border border-slate-700 hover:border-green-500 px-5 py-2.5 rounded-full shadow-lg backdrop-blur-sm"
            >
              <span className="text-xs font-black tracking-[0.2em] uppercase">Back to Quests</span>
            </button>
          )}

          {/* Food Progress Tracking - moved above divider */}
          <div className="bg-black/30 backdrop-blur-md rounded-xl p-6">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Utensils className="text-orange-400" />
              Food Progress ({eatenFoodItems.length}/{activeQuest.foodItems.length})
            </h3>

            <div className="space-y-6">
              {eatenFoodItems.length === 0 ? (
                <div className="text-center text-slate-400 py-8">
                  <p>Start eating to see your food progress!</p>
                  <p className="text-sm mt-2">Food items will appear as you progress...</p>
                </div>
              ) : (
                eatenFoodItems.map((food, index) => (
                  <div key={food.id} className="bg-white/10 rounded-lg p-4">
                    {/* Food text content with image/emoji */}
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-slate-800 flex items-center justify-center border border-white/10">
                        {food.image && food.image.startsWith('http') ? (
                          <img
                            src={food.image}
                            alt={food.name}
                            className="w-full h-full object-cover"
                            crossOrigin="anonymous"
                            referrerPolicy="no-referrer"
                            onLoad={() => handleImageLoad(food.id, 'direct')}
                            onError={(e) => handleImageError(e, food.id, food.image, food.emoji || '🍽️')}
                          />
                        ) : (
                          <span className="text-2xl">{food.emoji || '🍽️'}</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-white mb-2">{food.name}</h4>
                        <p className="text-sm text-slate-300 mb-3">{food.description}</p>

                        {/* Ingredients */}
                        <div className="mb-3">
                          <p className="text-xs text-slate-400 mb-1">Ingredients:</p>
                          <div className="flex flex-wrap gap-1">
                            {food.ingredients.map((ingredient, idx) => (
                              <span key={idx} className="text-xs bg-blue-900/30 text-blue-300 px-2 py-1 rounded">
                                {ingredient}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Allergens */}
                        {food.allergens && food.allergens.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs text-red-400 mb-1">⚠️ Allergens:</p>
                            <div className="flex flex-wrap gap-1">
                              {food.allergens.map((allergen, idx) => (
                                <span key={idx} className="text-xs bg-red-900/30 text-red-300 px-2 py-1 rounded">
                                  {allergen}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Cooking Method */}
                        {food.cookingMethod && (
                          <div className="mb-3">
                            <p className="text-xs text-green-400">🔥 {food.cookingMethod}</p>
                          </div>
                        )}

                        {/* Nutrition information removed - shown in totals below */}
                      </div>
                    </div>

                    {/* Food photo panel - only show if image is loaded */}
                    {food.image && food.image.startsWith('http') && imageLoadingStates[food.id] === 'direct' && (
                      <div className="bg-black/20 rounded-lg p-4">
                        <div className="w-full rounded-lg overflow-hidden bg-slate-800 flex items-center justify-center border border-white/10 relative">
                          {/* CartoonEffectCanvas disabled - using regular img instead */}
                          <img
                            src={food.image}
                            alt={food.name}
                            className="w-full object-contain"
                            crossOrigin="anonymous"
                            referrerPolicy="no-referrer"
                          />
                          {/* <CartoonEffectCanvas
                            imageSrc={food.image}
                            alt={food.name}
                            className="w-full object-contain"
                            levels={5}
                            edgeIntensity={5}
                            glowStrength={0.3}
                            animationSpeed={1.0}
                          /> */}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Nutrition Totals - moved above divider */}
            {eatenFoodItems.length > 0 && (
              <div className="mt-6 mb-4">
                <div className="grid grid-cols-4 gap-2 text-sm font-semibold">
                  <div className="text-center">
                    <span className="text-red-300">Total: {eatenFoodItems.reduce((sum, item) => sum + item.calories, 0)} cal</span>
                  </div>
                  <div className="text-center">
                    <span className="text-blue-300">Protein: {eatenFoodItems.reduce((sum, item) => sum + item.protein, 0)}g</span>
                  </div>
                  <div className="text-center">
                    <span className="text-yellow-300">Carbs: {eatenFoodItems.reduce((sum, item) => sum + item.carbs, 0)}g</span>
                  </div>
                  <div className="text-center">
                    <span className="text-green-300">Fat: {eatenFoodItems.reduce((sum, item) => sum + item.fat, 0)}g</span>
                  </div>
                </div>
              </div>
            )}

            {/* Divider line */}
            {eatenFoodItems.length > 0 && (
              <div className="border-t border-white/20"></div>
            )}
          </div>
        </div>
      </div>
    );
  }



  return (
    <div className="h-full w-full bg-slate-950 flex flex-col items-center justify-center p-4">
      {/* Exit Button - positioned like other components */}
      <ExitButton onExit={() => onNavigate('CITY')} />

      <div className="max-w-4xl w-full glass-panel rounded-lg shadow-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {getStandIcon(config.standIcon)}
            <div>
              <h1 className="text-3xl font-bold text-white">{config.standName}</h1>
              <p className="text-slate-300">{config.cuisine} Street Food Adventures</p>
            </div>
          </div>
        </div>

        <div className="grid gap-4 mb-6 max-h-96 overflow-y-auto pr-2">
          {quests.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Utensils className="mx-auto mb-4 opacity-20" size={48} />
              <p className="text-xl">No street food quests available</p>
              <p className="mt-2 text-sm italic">Check back later for new culinary adventures!</p>
            </div>
          ) : quests.map((quest) => (
            <div
              key={quest.id}
              className="bg-black/30 backdrop-blur-md rounded-xl p-6 hover:bg-black/40 transition-all cursor-pointer"
              onClick={() => startQuest(quest)}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    {getQuestIcon(quest.id)}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-800 flex items-center justify-center border border-white/10 shadow-inner">
                      <span className="text-2xl">{quest.foodItems[0]?.emoji || '🍽️'}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-1">{quest.name}</h3>
                      <p className="opacity-90 line-clamp-2 text-sm">{quest.description}</p>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`font-bold ${getDifficultyColor(quest.difficulty)}`}>
                    {quest.difficulty}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <MapPin size={14} />
                  <span>{quest.location}</span>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-white/60 font-medium">
                  <div className="flex items-center gap-1">
                    <DollarSign size={10} />
                    <span>{((quest?.reward || 0) * (RANKS[playerRank]?.multiplier || 1)).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users size={10} />
                    <span>{((quest?.fanReward || 0) * (RANKS[playerRank]?.multiplier || 1)).toLocaleString()} fans</span>
                  </div>
                  {(RANKS[playerRank]?.multiplier || 1) > 1 && (
                    <span className="text-cyan-400 font-bold">({RANKS[playerRank]?.multiplier}x)</span>
                  )}
                </div>
              </div>

              <div className="flex justify-end items-center">
                {quest.completed && (
                  <span className="px-3 py-1 bg-green-600 text-white text-xs rounded-full font-bold">
                    COMPLETED
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="text-center text-slate-400 text-sm">
          <p>Select a street food adventure to begin your culinary journey!</p>
          <p className="mt-1">Each quest costs $50 to start but offers great rewards!</p>
          <p className="mt-2 text-xs">📜 Scroll down to see all available quests</p>
        </div>
      </div>
    </div>
  );
};
